import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useLanguage } from '../../contexts/LanguageContext';
import { StockCount } from '../../types/stockCounts.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface StockCountPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  stockCount: StockCount | null;
  onEdit?: () => void;
}

const StockCountPreviewDialog: React.FC<StockCountPreviewDialogProps> = ({
  open,
  onClose,
  stockCount,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }
  }, [open]);

  if (!stockCount) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'InProgress': return isRTL ? 'قيد التنفيذ' : 'In Progress';
      case 'Completed': return isRTL ? 'مكتمل' : 'Completed';
      case 'Approved': return isRTL ? 'معتمد' : 'Approved';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'InProgress': return 'default';
      case 'Completed': return 'warning';
      case 'Approved': return 'success';
      case 'Posted': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatNumber = (num?: number) => (num ?? 0).toFixed(3);

  const canEdit = stockCount.status === 'InProgress' || stockCount.status === 'Completed';

  const getLocation = () => {
    if (stockCount.branchId) return isRTL ? (stockCount.branchNameAr || stockCount.branchName) : stockCount.branchName;
    if (stockCount.warehouseId) return isRTL ? (stockCount.warehouseNameAr || stockCount.warehouseName) : stockCount.warehouseName;
    return '-';
  };

  const getLocationType = () => {
    if (stockCount.branchId) return isRTL ? 'فرع' : 'Branch';
    if (stockCount.warehouseId) return isRTL ? 'مستودع' : 'Warehouse';
    return '-';
  };

  const totalVarianceValue = stockCount.details?.reduce((s, d) => s + (d.varianceValue || 0), 0) || 0;

  const handlePrint = () => {
    const themeColor = '#00695c';

    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid ${themeColor};padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:${themeColor};margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'جرد المخزون' : 'Stock Count';

    const detailRows = stockCount.details && stockCount.details.length > 0
      ? stockCount.details.map((d, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td><strong>${d.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? d.itemNameAr : d.itemName}</span></td>
          <td>${isRTL ? d.unitNameAr : d.unitName}</td>
          <td style="text-align:center;">${formatNumber(d.systemQuantity)}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.physicalQuantity)}</td>
          <td style="text-align:center;font-weight:bold;color:${(d.variance || 0) >= 0 ? '#2e7d32' : '#d32f2f'};">${formatNumber(d.variance)}</td>
          <td style="text-align:center;font-weight:bold;color:${(d.varianceValue || 0) >= 0 ? '#2e7d32' : '#d32f2f'};">${formatNumber(d.varianceValue)}</td>
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="8" style="text-align:center;color:#999;">${isRTL ? 'لا توجد تفاصيل' : 'No details'}</td></tr>`;

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle} - ${stockCount.countNumber}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 13px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px 10px; font-size: 12px; }
          th { background: ${themeColor}; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .info-item label { font-size: 11px; color: #999; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .location-box { padding: 12px; border-radius: 6px; text-align: center; background: #e0f2f1; border: 1px solid #80cbc4; margin: 15px 0; }
          .status { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
          .status-InProgress { background: #9e9e9e; }
          .status-Completed { background: #ed6c02; }
          .status-Approved { background: #2e7d32; }
          .status-Posted { background: #0288d1; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #000; padding-top: 5px; margin-top: 60px; }
          @page { size: A4; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:${themeColor};margin:10px 0 20px;">${docTitle}</h1>

        <div style="border:2px solid ${themeColor};border-radius:8px;padding:15px;margin-bottom:20px;">
          <div class="info-grid">
            <div class="info-item">
              <label>${isRTL ? 'رقم الجرد' : 'Count Number'}</label>
              <span style="color:${themeColor};">${stockCount.countNumber}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'التاريخ' : 'Date'}</label>
              <span>${formatDate(stockCount.countDate)}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'النوع' : 'Type'}</label>
              <span>${stockCount.countType || '-'}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'الحالة' : 'Status'}</label>
              <span class="status status-${stockCount.status}">${getStatusLabel(stockCount.status)}</span>
            </div>
            ${stockCount.journalEntryId ? `
            <div class="info-item">
              <label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label>
              <span style="color:#2e7d32;">${stockCount.journalEntryNumber || ''} (#${stockCount.journalEntryId})</span>
            </div>` : ''}
          </div>

          <hr style="border:none;border-top:1px solid #ddd;margin:10px 0;" />

          <div class="location-box">
            <div style="font-size:11px;color:#999;">${getLocationType()}</div>
            <div style="font-weight:bold;color:${themeColor};font-size:15px;">${getLocation()}</div>
          </div>

          ${stockCount.notes ? `<div style="margin-top:10px;"><label style="font-size:11px;color:#999;">${isRTL ? 'ملاحظات:' : 'Notes:'}</label><p>${stockCount.notes}</p></div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${isRTL ? 'الصنف' : 'Item'}</th>
              <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
              <th>${isRTL ? 'كمية النظام' : 'System Qty'}</th>
              <th>${isRTL ? 'الكمية الفعلية' : 'Physical Qty'}</th>
              <th>${isRTL ? 'الفرق' : 'Variance'}</th>
              <th>${isRTL ? 'قيمة الفرق' : 'Variance Value'}</th>
              <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            <tr style="background:#e0f2f1;font-weight:bold;">
              <td colspan="6" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي قيمة الفروقات:' : 'Total Variance Value:'}</td>
              <td style="text-align:center;color:${totalVarianceValue >= 0 ? '#2e7d32' : '#d32f2f'};">${formatNumber(totalVarianceValue)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line">${isRTL ? 'أمين المستودع' : 'Warehouse Keeper'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'المشرف' : 'Supervisor'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        </div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #004d40 0%, #00695c 100%)'
            : 'linear-gradient(135deg, #00695c 0%, #26a69a 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon />
          <Box component="span">{isRTL ? 'معاينة الجرد' : 'Stock Count Preview'}</Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم الجرد' : 'Count Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#00695c' }}>{stockCount.countNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{formatDate(stockCount.countDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'النوع' : 'Type'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{stockCount.countType || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box><Chip label={getStatusLabel(stockCount.status)} color={getStatusColor(stockCount.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {stockCount.journalEntryId && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم القيد' : 'Journal Entry'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{stockCount.journalEntryNumber || ''} (#{stockCount.journalEntryId})</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Location */}
          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(0,105,92,0.15)' : '#e0f2f1', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {getLocationType()}
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ color: '#00695c' }}>
              {getLocation()}
            </Typography>
          </Box>

          {stockCount.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">{stockCount.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(0,105,92,0.4)' : '#00695c', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? 'تفاصيل الأصناف' : 'Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'كمية النظام' : 'System Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية الفعلية' : 'Physical Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الفرق' : 'Variance'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'قيمة الفرق' : 'Variance Value'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockCount.details && stockCount.details.length > 0 ? (
                  stockCount.details.map((d, idx) => (
                    <TableRow key={d.countDetailId || idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{d.itemCode}</strong><br />
                        <Typography variant="caption" color="text.secondary">{isRTL ? d.itemNameAr : d.itemName}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>{formatNumber(d.systemQuantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatNumber(d.physicalQuantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: (d.variance || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {formatNumber(d.variance)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: (d.varianceValue || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {formatNumber(d.varianceValue)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{d.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">{isRTL ? 'لا توجد تفاصيل' : 'No details'}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {stockCount.details && stockCount.details.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align={isRTL ? 'left' : 'right'} sx={{ fontWeight: 'bold', bgcolor: 'rgba(0,105,92,0.1)', fontSize: '0.85rem' }}>
                      {isRTL ? 'إجمالي قيمة الفروقات:' : 'Total Variance Value:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(0,105,92,0.1)', color: totalVarianceValue >= 0 ? 'success.main' : 'error.main' }}>
                      <Chip label={formatNumber(totalVarianceValue)} color={totalVarianceValue >= 0 ? 'success' : 'error'} size="small" />
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(0,105,92,0.1)' }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        {canEdit && onEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit} size="large" sx={{ color: '#00695c', borderColor: '#00695c' }}>
            {isRTL ? 'تعديل' : 'Edit'}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? 'linear-gradient(45deg, #004d40 30%, #00695c 90%)' : 'linear-gradient(45deg, #00695c 30%, #26a69a 90%)' }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCountPreviewDialog;
